import React, { useEffect, useMemo, useState } from 'react';
import { Download, X } from 'lucide-react';

const dismissedKey = 'rozops_pwa_install_dismissed';

function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export default function PWAInstallPrompt({ compact = false }) {
  const [installEvent, setInstallEvent] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissedKey) === '1');
  const [installed, setInstalled] = useState(() => isStandalone());
  const ios = useMemo(() => isIOS(), []);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
      localStorage.setItem(dismissedKey, '1');
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed || dismissed || (!installEvent && !ios)) return null;

  const dismiss = () => {
    localStorage.setItem(dismissedKey, '1');
    setDismissed(true);
  };

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice.catch(() => null);
    setInstallEvent(null);
  };

  return (
    <div className={`rounded-2xl border border-rozops-accent-light bg-white shadow-soft ${compact ? 'p-3' : 'p-4'}`} data-testid="pwa-install-prompt">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-rozops-accent-light text-rozops-accent grid place-items-center shrink-0">
          <Download className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-rozops-navy">Install RozOps</div>
          <div className="text-sm text-rozops-text-muted mt-0.5">
            {ios ? 'Use Share, then Add to Home Screen to install RozOps.' : 'Add RozOps to this device for faster access.'}
          </div>
          {!ios && (
            <button type="button" onClick={install} className="mt-3 rounded-xl bg-rozops-accent hover:bg-rozops-accent-dark px-4 py-2 text-sm font-semibold text-white">
              Install app
            </button>
          )}
        </div>
        <button type="button" onClick={dismiss} aria-label="Dismiss install prompt" className="text-rozops-text-muted hover:text-rozops-text">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
